import React from 'react';

const UserActionsPanel = ({ 
  permissions, 
  selectedUsers, 
  onUpdatePassword, 
  onUpdateStatus,
  onUpdateAccessLevel,
  onDefineProjectAccess
}) => {
  return (
    <div className="w-64 bg-white border rounded shadow-sm" style={{
      position: 'sticky',
      top: '1.5rem',
      height: 'fit-content',
      maxHeight: 'calc(100vh - 9rem)',
      overflowY: 'auto'
    }}>
      <div className="p-4">
        <h3 className="font-bold text-sm text-orange-700 mb-4 bg-orange-200">USER ACTIONS</h3>

        {/* User management buttons */}
        <div className="space-y-2 mb-6">
          {/* Update Password Button */}
          <button
            className={`w-full px-3 py-2 rounded text-sm flex items-center ${
              !permissions.canUpdate || selectedUsers.size === 0
                ? 'text-gray-400'
                : 'text-blue-700 hover:text-blue-900'
            }`}
            disabled={selectedUsers.size === 0 || !permissions.canUpdate}
            onClick={onUpdatePassword}
          >
            <img
              src="/icons/noun-password-6930926-FF824A.svg"
              alt="Update Password"
              className={`w-8 h-8 mr-3 ${!permissions.canUpdate || selectedUsers.size === 0 ? 'opacity-50' : ''}`}
            />
            <span className={`flex items-center ${!permissions.canUpdate || selectedUsers.size === 0 ? 'text-orange-400' : 'text-orange-700'}`}>
              Update Password
              <img
                src="/icons/noun-one-to-many-1406451-FF824A.svg"
                alt="Info"
                className="w-6 h-6 ml-2"
              />
            </span>
          </button>

          {/* Update Status Button */}
          <button
            className={`w-full px-3 py-2 rounded text-sm flex items-center ${
              !permissions.canUpdate || selectedUsers.size === 0
                ? 'text-gray-400'
                : 'text-blue-700 hover:text-blue-900'
            }`}
            disabled={selectedUsers.size === 0 || !permissions.canUpdate}
            onClick={onUpdateStatus}
          >
            <img
              src="/icons/noun-status-5702263-FF824A.svg"
              alt="Update Status"
              className={`w-10 h-10 mr-3 ${!permissions.canUpdate || selectedUsers.size === 0 ? 'opacity-50' : ''}`}
            />
            <span className={`flex items-center ${!permissions.canUpdate || selectedUsers.size === 0 ? 'text-orange-400' : 'text-orange-700'}`}>
              Update Status
              <img
                src="/icons/noun-one-to-many-1406451-FF824A.svg"
                alt="Info"
                className="w-6 h-6 ml-2"
              />
            </span>
          </button>

          {/* Update Access Level Button */}
          <button
            className={`w-full px-3 py-2 rounded text-sm flex items-center ${
              !permissions.canUpdate || selectedUsers.size === 0
                ? 'text-gray-400'
                : 'text-blue-700 hover:text-blue-900'
            }`}
            disabled={selectedUsers.size === 0 || !permissions.canUpdate}
            onClick={onUpdateAccessLevel}
          >
            <img
              src="/icons/noun-permissions-7242870-FF824A.svg"
              alt="Update Access Level"
              className={`w-9 h-9 mr-3 ${!permissions.canUpdate || selectedUsers.size === 0 ? 'opacity-50' : ''}`}
            />
            <span className={`flex items-center ${!permissions.canUpdate || selectedUsers.size === 0 ? 'text-orange-400' : 'text-orange-700'}`}>
              Update Default Access Level (across all projects)
              <img
                src="/icons/noun-one-to-many-1406451-FF824A.svg"
                alt="Info"
                className="w-6 h-6 ml-2"
              />
            </span>
          </button>

          {/* Define Project Level Access Button */}
          <button
            className={`w-full px-3 py-2 rounded text-sm flex items-center ${
              !permissions.canViewUserProjectList || selectedUsers.size === 0
                ? 'text-gray-400'
                : 'text-blue-700 hover:text-blue-900'
            }`}
            disabled={selectedUsers.size  === 0 || !permissions.canViewUserProjectList}
            onClick={onDefineProjectAccess}
          >
            <img
              src="/icons/noun-permission-6243347-FF824A.svg"
              alt="Manage Project Specific Access"
              className={`w-10 h-10 mr-3 ${!permissions.canViewUserProjectList || selectedUsers.size === 0 ? 'opacity-50' : ''}`}
            />
            <span className={`flex items-center ${!permissions.canViewUserProjectList || selectedUsers.size === 0  ? 'text-orange-400' : 'text-orange-700'}`}>
              Manage Project Specific Access
              <img
                src="/icons/noun-one-to-many-1406451-FF824A.svg"
                alt="Info"
                className="w-6 h-6 ml-2"
              />
            </span>
          </button>
        </div>

        {/* Selection Info */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
          <div className="font-medium text-blue-800 mb-1">Selection Info:</div>
          <div className="text-blue-600">
            {selectedUsers.size > 0 ? (
              <>
                <div>{selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected</div>
                {selectedUsers.size <= 3 && (
                  <div className="mt-1 text-xs">
                    {Array.from(selectedUsers).map((userId, index) => (
                      <div key={userId} className="truncate">
                        • {userId.length > 20 ? `${userId.substring(0, 20)}...` : userId}
                      </div>
                    ))}
                  </div>
                )}
                {selectedUsers.size > 3 && (
                  <div className="mt-1 text-xs">
                    • {Array.from(selectedUsers)[0].length > 20 ? `${Array.from(selectedUsers)[0].substring(0, 20)}...` : Array.from(selectedUsers)[0]}
                    <div>• and {selectedUsers.size - 1} more...</div>
                  </div>
                )}
              </>
            ) : (
              'No users selected'
            )}
          </div>
        </div>

        {/* Permission Info */}
        {!permissions.canUpdate && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
            <div className="font-medium text-yellow-800 mb-1">Permissions:</div>
            <div className="text-yellow-600">
              You do not have permission to update users. Contact your administrator to request access.
            </div>
          </div>
        )}

        <div className="flex items-center text-xs text-gray-500 italic mt-4">
          <img
            src="/icons/noun-one-to-many-1406451-676767.svg"
            alt="Info"
            className="w-6 h-6 ml-2"
          />
          -Supports multiple row selection
        </div>
      </div>
    </div>
  );
};

export default UserActionsPanel;